import type { ComboboxOption } from "@v1/components/designSystem/combobox/ComboboxContext";

import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import Popover from "@v1/components/designSystem/popover/Popover";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import { ChevronDown, CornerDownLeft, Lock, Search } from "lucide-react";

type PageUrlSelectProps = {
  isEditable: boolean;
  target: string | null;
  onChange: (value: string) => void;
  options: ComboboxOption[];
};
type PageUrlType = "replo" | "custom";

type ReploPageComboboxProps = {
  options: ComboboxOption[];
  target: string | null;
  onChange: (value: string) => void;
  setIsPopoverOpen: (value: boolean) => void;
  UNSAFE_className?: string;
};

const ReploPageCombobox = ({
  options,
  target,
  onChange,
  setIsPopoverOpen,
  UNSAFE_className,
}: ReploPageComboboxProps) => {
  const [input, setInput] = React.useState("");

  return (
    <Combobox.Root
      options={options}
      value={target ?? ""}
      onChange={(value) => {
        onChange(value);
        setInput("");
        setIsPopoverOpen(false);
      }}
      input={input}
      onInputChange={(value) => setInput(value)}
      syncInputWithSelectedValue={false}
    >
      <Combobox.Trigger>
        <Combobox.Input
          placeholder="Search published Replo pages..."
          startEnhancer={<Search className="h-3 w-3" />}
          outlineOnActive={false}
          size="sm"
        />
      </Combobox.Trigger>
      <Combobox.Content UNSAFE_listClassName={UNSAFE_className} />
    </Combobox.Root>
  );
};

type CustomPageInputProps = {
  onChange: (value: string) => void;
  setIsPopoverOpen: (value: boolean) => void;
};

const CustomPageInput = ({
  onChange,
  setIsPopoverOpen,
}: CustomPageInputProps) => {
  const [input, setInput] = React.useState("");

  const onAddCustomURL = (url: string) => {
    if (url) {
      onChange(url);
      setInput("");
      setIsPopoverOpen(false);
    }
  };

  return (
    <DeprecatedInput
      value={input}
      onChange={(e) => {
        setInput(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && input) {
          onAddCustomURL(input);
        }
      }}
      placeholder="Enter your page URL"
      layoutClassName="h-8"
      size="sm"
      endEnhancer={() => (
        <CornerDownLeft
          size={13}
          className="text-muted cursor-pointer"
          onClick={() => {
            onAddCustomURL(input);
          }}
        />
      )}
    />
  );
};

const PageUrlSelect: React.FC<PageUrlSelectProps> = ({
  isEditable,
  target,
  onChange,
  options,
}) => {
  const popoverTriggerRef = React.useRef<HTMLDivElement>(null);

  const [pageUrlType, setPageUrlType] = React.useState<PageUrlType>("replo");

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [popoverContentWidth, setPopoverContentWidth] =
    React.useState<number>(0);

  React.useEffect(() => {
    if (popoverTriggerRef.current) {
      setPopoverContentWidth(popoverTriggerRef.current.offsetWidth);
    }
  }, []);

  return (
    <Popover.Root
      isOpen={isPopoverOpen}
      onOpenChange={(value) => {
        if (isEditable) {
          setIsPopoverOpen(value);
        }
      }}
    >
      <Popover.Trigger asChild>
        <div ref={popoverTriggerRef}>
          <div
            className={twMerge(
              "bg-sheet border-0.5 border-component-border flex h-8 cursor-pointer items-center justify-between gap-1 rounded-sm px-2 text-xs",
              !isEditable && "cursor-not-allowed opacity-50",
            )}
          >
            <div className="truncate text-ellipsis">
              <span className={twMerge("", !target && "text-placeholder")}>
                {target ?? "Select a page"}
              </span>
            </div>
            {isEditable ? (
              <ChevronDown className="shrink-0" size={16} />
            ) : (
              <Lock className="shrink-0" size={12} />
            )}
          </div>
        </div>
      </Popover.Trigger>
      <Popover.Content
        side="bottom"
        style={{ width: popoverContentWidth }}
        className="p-4"
        shouldPreventDefaultOnInteractOutside={false}
        collisionPadding={10}
        hideCloseButton
      >
        <div className="flex flex-col gap-2">
          <div>
            <DeprecatedToggleGroup
              size="sm"
              options={[
                {
                  label: "Replo Pages",
                  value: "replo",
                },
                {
                  label: "Custom URL",
                  value: "custom",
                },
              ]}
              selectedValue={pageUrlType}
              onChange={(value) => {
                setPageUrlType(value as PageUrlType);
              }}
            />
          </div>
          {pageUrlType === "replo" ? (
            <ReploPageCombobox
              options={options}
              target={target}
              onChange={onChange}
              setIsPopoverOpen={setIsPopoverOpen}
              UNSAFE_className="max-h-[calc(var(--radix-popover-content-available-height)-100px)]"
            />
          ) : (
            <CustomPageInput
              onChange={onChange}
              setIsPopoverOpen={setIsPopoverOpen}
            />
          )}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

export { PageUrlSelect };
